import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { API } from '@models/API';

import { patchableSignal } from '@utils/signals/patchableSignal';

/**
 * Element-wise comparison of two priority sets. Both sides are already
 * deduplicated and sorted ascending, so the order is deterministic and a
 * positional walk is enough.
 */
const samePriorities = (a: number[], b: number[]) =>
  a.length === b.length && a.every((priority, i) => priority === b[i]);

@Injectable({ providedIn: 'root' })
export class OperationsState {
  private router = inject(Router);

  public _operations = patchableSignal<API.Trusts.Operations.Response>({
    items: [],
    total: 0,
    page: 0,
    size: 0,
    pages: 0,
  });

  private _id = signal('');
  private _isActive = signal(false);
  private _activeChild = signal<string>('');
  private _selectedPriorities = signal<number[] | null>(null);

  activeOperation = computed(() => {
    const operations = this.get().items;
    const operation = operations.find(op => op.trust_number === this._id())!;
    return operation;
  });

  availableTopics = computed(
    () => this.activeOperation()?.available_topics ?? []
  );

  private defaultPriority = computed(() => {
    const topics = this.availableTopics();
    return topics.length ? Math.min(...topics.map(t => t.priority)) : 0;
  });

  /**
   * The Sections the user has selected. Defaults to the lowest available
   * priority. Never empty.
   *
   * `equal` keeps the previous array when the resulting set is unchanged, so
   * re-selecting the same Sections is not a notification. Consumers fan a
   * change out into one request per Section, so a spurious notification costs
   * N redundant HTTP calls.
   */
  selectedPriorities = computed(
    () => {
      const selection = this._selectedPriorities() ?? [this.defaultPriority()];
      const topics = this.availableTopics();

      // No topics means the operations list has not loaded yet (or was
      // cleared), not that every Section is gone: there is nothing to clamp
      // against, so hold the selection until the refetch lands. Same reading
      // as `defaultPriority`, which falls back to 0 in that window.
      if (!topics.length) {
        return selection;
      }

      // A Section the user had selected can disappear on a refetch (a facility
      // edit re-issues the list without navigating). Drop what no longer
      // exists, and never let the set end up empty.
      const available = new Set(topics.map(t => t.priority));
      const existing = selection.filter(priority => available.has(priority));

      return existing.length ? existing : [this.defaultPriority()];
    },
    { equal: samePriorities }
  );

  /**
   * Lowest selected priority. Kept as a scalar because most consumers still
   * drive a single-Section request off it.
   */
  selectedPriority = computed(() => Math.min(...this.selectedPriorities()));

  selectedTopic = computed(
    () =>
      this.availableTopics().find(t => t.priority === this.selectedPriority())
        ?.topic ?? null
  );

  constructor() {
    this.router.events.subscribe(val => {
      if (val instanceof NavigationEnd) {
        const operationIsActive = this.router.isActive(
          '/dashboard/portfolio/facility',
          {
            paths: 'subset',
            queryParams: 'ignored',
            fragment: 'ignored',
            matrixParams: 'ignored',
          }
        );

        if (operationIsActive) {
          const url = this.router.url.split('/');

          if (url.length >= 6) {
            const previousId = this._id();
            this._isActive.set(true);
            try {
              this._id.set(decodeURIComponent(url[4]).replace('-', '/'));
            } catch {
              this._id.set(url[4].replace('-', '/'));
            }
            this._activeChild.set(url[5]);
            if (this._id() !== previousId) {
              this._selectedPriorities.set(null);
            }
            return;
          }
        }

        this._isActive.set(false);
        this._id.set('');
        this._activeChild.set('');
        this._selectedPriorities.set(null);
      }
    });
  }

  get: Signal<API.Trusts.Operations.Response> = this._operations.asReadonly();

  set(operations: API.Trusts.Operations.Response) {
    this._operations.set(operations);
  }

  /**
   * Empties the cached list. Needed after a facility mutation: operationGuard
   * short-circuits on a known trust_number, so a stale entry here would keep
   * the detail pages showing pre-edit values.
   */
  clear() {
    this._operations.set({ items: [], total: 0, page: 0, size: 0, pages: 0 });
  }

  patch(values: Partial<API.Trusts.Operations.Response>) {
    this._operations.patch(values);
  }

  activeId = this._id.asReadonly();

  isActive = this._isActive.asReadonly();

  isIdActive(id: string) {
    return id.replace('/', '-') === this.activeId().replace('/', '-');
  }

  selectPriority(priority: number) {
    this.selectPriorities([priority]);
  }

  /**
   * Replaces the whole selection. An empty set is ignored: `selectedPriority`
   * has to stay a valid number for the consumers that still read it as a
   * scalar, so at least one Section must always be selected.
   */
  selectPriorities(priorities: number[]) {
    const next = [...new Set(priorities)].sort((a, b) => a - b);

    if (!next.length) {
      return;
    }

    this._selectedPriorities.set(next);
  }

  // Refers to which sub-page is active
  activeChild = this._activeChild.asReadonly();
}
